import * as React from 'react';
import * as adminStyles from '../AdminStyles.css';
import { inject, observer } from 'mobx-react';
import { NavigatorStore } from '../../stores';
import { IAIcon } from '../../../icon/Icon';
import { Helper } from '../../../../Helper';
import { IADateAndTimePicker } from '../../../dateAndTimePicker/DateAndTimePicker';
import { IAButton } from '../../../button/Button';
import AnalyticsService from '../../../../services/AnalyticsService';
import DateTimeService from '../../../../services/DateTimeService';
import { IASpinner } from '../../../spinner/Spinner';
import { IAPopup } from '../../../popup/Popup';
import { LocalizationService } from '../../../../services/LocalizationService';
import { IAMessageBar } from '../../../messageBar/MessageBar';
import { IADropDown } from '../../../dropDown/DropDown';

export interface IAnalyticsPanelProps {
  navigatorStore?: NavigatorStore;
}

export interface IAnalyticsPanelState {
  lastFromDate: number;
  lastFromMonth: number;
  lastToDate: number;
  lastToMonth: number;
  fromDate: Date;
  toDate: Date;
}
@inject('navigatorStore')
@observer
export default class AnalyticsPanel extends React.Component<IAnalyticsPanelProps, IAnalyticsPanelState> {

  private readonly localizationService: LocalizationService = new LocalizationService();
  private readonly dateTimeService: DateTimeService = new DateTimeService();

  constructor(props: IAnalyticsPanelProps) {
    super(props);
    this.state = {
      lastFromDate: this.props.navigatorStore.fromDate.getDate(),
      lastFromMonth: this.props.navigatorStore.fromDate.getMonth(),
      lastToDate: this.props.navigatorStore.toDate.getDate(),
      lastToMonth: this.props.navigatorStore.toDate.getMonth(),
      fromDate: this.props.navigatorStore.fromDate,
      toDate: this.props.navigatorStore.toDate,
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public render(): JSX.Element {
    let top = 50;
    switch (Helper.getDisplayEnvironment()) {
      case "ExternalEnvironment":
        top = 50;
        break;
      case "Sharepoint":
        top = 161;
        break;
    }
    if (this.props.navigatorStore.navigator?.design?.menuBar?.navigatorType === "modern") {
      top = top + 40;
    }
    return (
      <div
        className={adminStyles.panel}
        style={{
          right: this.props.navigatorStore.isShowingAnalytics ? "0px" : "-400px",
          top: top,
          height: `calc(100% - ${top}px)`,
          opacity: this.props.navigatorStore.isDownloading ? "0.7" : "1"
        }}
      >
        <div className={adminStyles.panelHeader}>
          {this.localizationService.strings.Navigator_Analytics_Title}
          <IAIcon
            title={"Close"}
            size={20}
            color="#ffffff"
            onClick={() => this.props.navigatorStore.toggleAnalytics()}
            style={{
              float: "right",
              padding: "5px 10px",
              cursor: "pointer"
            }}
          />
        </div>
        <div className={adminStyles.content}>
          {this.localizationService.strings.Navigator_Analytics_Info}
          <div className={adminStyles.panelDivider}
            style={{
              marginTop: "20px"
            }}
          />
          <div>
            <IADateAndTimePicker
              label={this.localizationService.strings.Navigator_Analytics_StartDate}
              selectedDate={this.state.fromDate}
              showTimeSelector={false}
              highlightColor={this.props.navigatorStore.highlightColor}
              isClearable={false}
              onChange={(fromDate: Date) => {
                if (this.state.lastFromDate == fromDate.getDate() && fromDate.getMonth() != this.state.lastFromMonth) {
                  this.setState({ lastFromDate: fromDate.getDate(), lastFromMonth: fromDate.getMonth(), fromDate: undefined });
                } else {
                  this.setState({ fromDate: fromDate }, () => {
                    if (this.state.fromDate.getDate() != undefined) {
                      this.props.navigatorStore.getNavigatorClicks(this.state.fromDate, this.state.toDate);
                    }
                  });
                }
                if (!isNaN(fromDate.getDate())) {
                  this.setState({ lastFromDate: fromDate.getDate() });
                }
              }}
            />
            <IADateAndTimePicker
              label={this.localizationService.strings.Navigator_Analytics_EndDate}
              selectedDate={this.state.toDate}
              showTimeSelector={false}
              highlightColor={this.props.navigatorStore.highlightColor}
              isClearable={false}
              onChange={(toDate: Date) => {
                if (this.state.lastToDate == toDate.getDate() && toDate.getMonth() != this.state.lastToMonth) {
                  this.setState({ lastToDate: toDate.getDate(), lastToMonth: toDate.getMonth(), toDate: undefined });
                } else {
                  this.setState({ toDate: toDate }, () => {
                    if (this.state.toDate.getDate() != undefined) {
                      this.props.navigatorStore.getNavigatorClicks(this.state.fromDate, this.state.toDate);
                    }
                  });
                }
                if (!isNaN(toDate.getDate())) {
                  this.setState({ lastToDate: toDate.getDate() });
                }
              }}
            />
            <IAButton
              borderRadius={5}
              label={this.localizationService.strings.Navigator_Analytics_ExportToExcel}
              onClick={() => {
                this.props.navigatorStore.isDownloading = true;
                AnalyticsService.downloadExcelExportNavigator(
                  this.props.navigatorStore.environment,
                  this.props.navigatorStore.token,
                  this.props.navigatorStore.tenant,
                  this.props.navigatorStore.instance,
                  this.dateTimeService.ConvertToDDMMYYYYWithSlashes(this.state.fromDate),
                  this.dateTimeService.ConvertToDDMMYYYYWithSlashes(this.state.toDate),
                  "total")
                  .then(() => {
                    this.props.navigatorStore.isDownloading = false;
                  }).catch(() => {
                    this.props.navigatorStore.isDownloading = false;
                    this.props.navigatorStore.downloadFailed = true;
                  })
              }}
              style={{
                marginTop: 20,
              }}
              height={40}
              iconMarginTop={12}
              buttonColor="#f4f4f4"
              arialable="Open"
              icon="Export to excel"
            />
          </div>
          <div className={adminStyles.panelDivider}
            style={{
              marginTop: "20px"
            }}
          />
          <div className={adminStyles.panelSecondHeader}>
            {this.localizationService.strings.Navigator_Analytics_VisualizationHeadline}
          </div>
          {this.props.navigatorStore.doneSettingClicks && this.props.navigatorStore.areThereClicksInPeriod &&
            <IADropDown
              selectedOption={this.props.navigatorStore.clickFilter}
              borderRadius={5}
              options={[
                {
                  key: "Total",
                  value: this.localizationService.strings.Navigator_Analytics_TotalClicks
                },
                {
                  key: "Unique",
                  value: this.localizationService.strings.Navigator_Analytics_UniqueClicks
                }
              ]}
              onChange={(clickFilter) => {
                this.props.navigatorStore.clickFilter = clickFilter;
              }}
            >
            </IADropDown>
          }
          {!this.props.navigatorStore.doneSettingClicks && !this.props.navigatorStore.visualizationFailed &&
            <IASpinner
              style={{
                width: "32px",
                height: "32px",
                float: "left",
                marginTop: "100px"
              }}
            />
          }
          {this.props.navigatorStore.visualizationFailed &&
            <div style={{ float: "left" }}>
              <IAMessageBar
                icon="Error"
                showIcon={true}
                type="error"
                content={this.localizationService.strings.Navigator_Analytics_VisualizationFailed}
                onDismiss={() => { }}
                style={{ position: "relative", marginTop: "20px" }}
              />
              <IAButton
                borderRadius={5}
                label={this.localizationService.strings.Navigator_Analytics_RetryButton}
                onClick={() => {
                  this.props.navigatorStore.getNavigatorClicks(this.state.fromDate, this.state.toDate);
                }}
                style={{
                  marginRight: 155,
                  marginTop: 20,
                }}
                height={40}
                iconMarginTop={8}
                icon='Refresh'
                buttonColor="#f4f4f4"
              />
            </div>
          }
          {this.props.navigatorStore.doneSettingClicks && !this.props.navigatorStore.areThereClicksInPeriod &&
            <div>
              <IAMessageBar
                icon="Error"
                showIcon={true}
                type="information"
                content={this.localizationService.strings.Navigator_Analytics_NoClickDataToShow}
                onDismiss={() => { }}
                style={{ position: "relative", marginTop: "20px" }}
              />
            </div>
          }
          {this.props.navigatorStore.isDownloading && <IASpinner />}
          {this.props.navigatorStore.downloadFailed &&
            <IAPopup
              headline={this.localizationService.strings.Navigator_Analytics_ExcelDownloadError}
              content={
                <IAButton
                  label={this.localizationService.strings.Navigator_Analytics_ClosePopup}
                  buttonColor={this.props.navigatorStore.highlightColor}
                  showSpinner={false}
                  borderRadius={5}
                  onClick={() => {
                    this.props.navigatorStore.downloadFailed = false;
                  }}
                  style={{
                    marginTop: 20,
                    float: "left"
                  }}
                />
              }
            />
          }
        </div>
      </div>
    );
  }
}